/**
 * Analytics helper utilities for data processing and conversion
 */

/**
 * Safely convert base64 string to Blob for file downloads
 * Uses Uint8Array to handle unicode characters properly
 */
export const base64ToBlob = (base64: string, mime: string): Blob => {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mime });
};

/**
 * Detect if a column contains predominantly numeric data
 * Samples first 10 rows and checks if 70% or more are valid numbers
 */
export const isNumericColumn = (rows: any[], colKey: string): boolean => {
    if (!rows || rows.length === 0) return false;
    const sample = rows.slice(0, 10);
    let numericCount = 0;
    
    for (const row of sample) {
        const val = row[colKey];
        if (val === null || val === undefined || val === '') continue;
        const cleaned = String(val).replace(/[$,]/g, '').trim();
        if (!isNaN(parseFloat(cleaned)) && isFinite(parseFloat(cleaned))) {
            numericCount++;
        }
    }
    
    return numericCount >= sample.length * 0.7;
};

/**
 * Column type definition for analytics data
 */
export type AnalyticsColumn = { 
    key: string; 
    label?: string; 
    type?: 'string' | 'number' | 'date' 
};

/**
 * Normalize column schema for consistent access
 * Handles both string columns and object columns with key/label properties
 * Removes BOM characters (U+FEFF) from keys and labels
 */
export const normalizeColumns = (columns: any[] | undefined): Array<{ key: string; label: string }> => {
    if (!columns) return [];
    
    return columns.map(col => {
        if (typeof col === 'string') {
            const cleanKey = col.replace(/^\uFEFF/, '');
            return { key: cleanKey, label: cleanKey };
        }
        // Try multiple property names for the column key
        const key = (col.key ?? col.name ?? col.label ?? String(col)).replace(/^\uFEFF/, '');
        const label = (col.label ?? col.name ?? col.key ?? String(col)).replace(/^\uFEFF/, '');
        return { key, label };
    });
};
